import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { CourseProgressProvider } from "@/features/course";
import { AuthProvider, ProtectedRoute, RoleBasedRoute } from "@/features/auth";
import { HealthCheckInProvider } from "@/context/HealthCheckInContext";
import { Layout } from "@/components/layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Retry dynamic imports — handles stale chunks after deployments
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk failed to load (stale hash after deploy) — reload once
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      // If we already reloaded, surface the error
      return importFn();
    })
  );
}

// Static imports - public pages (always in main bundle)
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFound from "./pages/NotFound";

const CertificatePage = lazyWithRetry(() => import("./pages/CertificatePage"));

// Lazy imports - auth pages
const LoginPage = lazyWithRetry(() => import("@/features/auth/pages/LoginPage"));
const RegisterPage = lazyWithRetry(() => import("@/features/auth/pages/RegisterPage"));
const MFAChallengePage = lazyWithRetry(() => import("@/features/auth/pages/MFAChallengePage"));

// Lazy imports - portal & chat
const PortalPage = lazyWithRetry(() => import("@/features/portal/pages/PortalPage"));
const ChatPage = lazyWithRetry(() => import("@/features/chat/pages/ChatPage"));

// Lazy imports - course pages
const CoursePage = lazyWithRetry(() => import("@/features/course/pages/CoursePage"));
const CourseOverviewPage = lazyWithRetry(() => import("@/features/course/pages/CourseOverviewPage"));
const ModulePage = lazyWithRetry(() => import("@/features/course/pages/ModulePage"));
const ChapterPage = lazyWithRetry(() => import("@/features/course/pages/ChapterPage"));
const RecommendationsPage = lazyWithRetry(() => import("@/features/course/pages/RecommendationsPage").then(m => ({ default: m.RecommendationsPage })));

// Lazy imports - assessment pages (heavy: forms, scoring, recharts)
const AssessmentPage = lazyWithRetry(() => import("@/features/assessment/pages/AssessmentPage").then(m => ({ default: m.AssessmentPage })));
const AssessmentResultsPage = lazyWithRetry(() => import("@/features/assessment/pages/AssessmentResultsPage").then(m => ({ default: m.AssessmentResultsPage })));
const Form2Page = lazyWithRetry(() => import("@/features/assessment/pages/Form2Page").then(m => ({ default: m.Form2Page })));

// Lazy imports - facilitator pages (heavy: recharts, client data)
const FacilitatorDashboard = lazyWithRetry(() => import("@/features/facilitator/pages/FacilitatorDashboard").then(m => ({ default: m.FacilitatorDashboard })));
const ClientDetailPage = lazyWithRetry(() => import("@/features/facilitator/pages/ClientDetailPage").then(m => ({ default: m.ClientDetailPage })));

// Lazy imports - admin pages (heavy: recharts, user management)
const AdminDashboard = lazyWithRetry(() => import("@/features/admin/pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const SuperAdminDashboard = lazyWithRetry(() => import("@/features/super-admin/pages/SuperAdminDashboard").then(m => ({ default: m.SuperAdminDashboard })));

// Lazy imports - other protected pages
const GDPRPage = lazyWithRetry(() => import("@/features/gdpr/pages/GDPRPage").then(m => ({ default: m.GDPRPage })));
const ClientProgressPage = lazyWithRetry(() => import("@/features/client/pages/ClientProgressPage").then(m => ({ default: m.ClientProgressPage })));

// Route loading fallback
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <LoadingSpinner label="Laddar..." />
  </div>
);

// Suspense wrapper for lazy routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoader />}>{children}</Suspense>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <HealthCheckInProvider>
      <CourseProgressProvider>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <SentryRoutes>
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="certifikat" element={<CertificatePage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                
                {/* Protected routes - require authentication */}
                <Route
                  path="portal"
                  element={
                    <ProtectedRoute>
                      <LazyRoute><RouteErrorBoundary><PortalPage /></RouteErrorBoundary></LazyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <LazyRoute><RouteErrorBoundary><PortalPage /></RouteErrorBoundary></LazyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stella"
                  element={
                    <ProtectedRoute>
                      <LazyRoute><RouteErrorBoundary><ChatPage /></RouteErrorBoundary></LazyRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Assessment - requires facilitator or admin role */}
                <Route
                  path="assessment"
                  element={
                    <RoleBasedRoute allowedRoles={['admin', 'facilitator']}>
                      <LazyRoute><RouteErrorBoundary><AssessmentPage /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="assessment/results/:formId"
                  element={
                    <RoleBasedRoute allowedRoles={['admin', 'facilitator', 'client']}>
                      <LazyRoute><RouteErrorBoundary><AssessmentResultsPage /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="assessment/form2/:assessmentId"
                  element={
                    <RoleBasedRoute allowedRoles={['admin', 'facilitator']}>
                      <LazyRoute><RouteErrorBoundary><Form2Page /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />

                {/* GDPR - accessible by all authenticated users */}
                <Route
                  path="gdpr"
                  element={
                    <ProtectedRoute>
                      <LazyRoute><RouteErrorBoundary><GDPRPage /></RouteErrorBoundary></LazyRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Facilitator - requires facilitator, admin, or super_admin role */}
                <Route
                  path="facilitator"
                  element={
                    <RoleBasedRoute allowedRoles={['facilitator', 'admin', 'local_admin', 'super_admin']}>
                      <LazyRoute><RouteErrorBoundary><FacilitatorDashboard /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="facilitator/client/:clientId"
                  element={
                    <RoleBasedRoute allowedRoles={['facilitator', 'admin', 'local_admin', 'super_admin']}>
                      <LazyRoute><RouteErrorBoundary><ClientDetailPage /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />

                {/* Super Admin - requires super_admin role only */}
                <Route
                  path="super-admin"
                  element={
                    <RoleBasedRoute allowedRoles={['super_admin']}>
                      <LazyRoute><RouteErrorBoundary><SuperAdminDashboard /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />

                {/* Admin - requires local_admin, admin, or super_admin role */}
                <Route
                  path="admin"
                  element={
                    <RoleBasedRoute allowedRoles={['local_admin', 'admin', 'super_admin']}>
                      <LazyRoute><RouteErrorBoundary><AdminDashboard /></RouteErrorBoundary></LazyRoute>
                    </RoleBasedRoute>
                  }
                />
              </Route>
              
              {/* Auth routes - redirect to portal if already logged in */}
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LazyRoute><LoginPage /></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LazyRoute><RegisterPage /></LazyRoute>
                  </ProtectedRoute>
                }
              />

              {/* MFA challenge - accessible while logged in (aal1), not wrapped in Layout */}
              <Route
                path="/mfa/challenge"
                element={
                  <LazyRoute><MFAChallengePage /></LazyRoute>
                }
              />

              {/* Course routes - require authentication */}
              <Route
                path="/course/:courseId"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><CoursePage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/course-overview"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><CourseOverviewPage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recommendations"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><RecommendationsPage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><ClientProgressPage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/modul/:moduleId"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><ModulePage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/modul/:moduleId/kapitel/:chapterId"
                element={
                  <ProtectedRoute>
                    <LazyRoute><RouteErrorBoundary><ChapterPage /></RouteErrorBoundary></LazyRoute>
                  </ProtectedRoute>
                }
              />
              
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
        </BrowserRouter>
      </TooltipProvider>
    </CourseProgressProvider>
    </HealthCheckInProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
